<!--
  Specialization of ElColorableInput for inputs

  In addition to ElColorableInput behavior:

  Emits:
  - @suggestion(val): requests a suggestion
      ex: null // no suggestion requested
      ex: { // suggestion requested
            key: "key", // the segment key
            type: "type", // the type of segment
            query: "text", // the text to suggest from
            input: true, // triggered directly by user input
            x: 123, // the x position on the screen
          }

  Methods:
  - insert(entity, key, start, stop, text):
      inserts a new entity or synonym segment "entity", inside the segment "key"
      from "start" to "stop" with the text "text"
  - unreference(key): replace thesegment "key" by a text segment
  - setRef(key, val): changes the entityid or synonymid of the segment "key"
  - setText(key, val): changes the text of the segment "key"
-->

<script>
import XRegExp from 'xregexp'
import ElColorableInput from './ElColorableInput.vue'

const GLOBAL_FIELDS = ['ref']
// to replace an entity whose text has been emptied outside
const PLACEHOLDER = '~'
const LETTERS = '\\p{M}\\p{L}\\p{N}'
const ENTITY = new XRegExp('^(.*[^L])?@([L]*)([^L].*)?$'.replace(/L/g, LETTERS), 'u')
const SYNONYM = new XRegExp('^([^L]*)([L](?:.*[L])?)([^L]*)$'.replace(/L/g, LETTERS), 'u')
const TEXT = new XRegExp('([^@]+)|@([L]*)'.replace(/L/g, LETTERS), 'ug')

export default {
  name: 'ElUtteranceInput',
  extends: ElColorableInput,

  data () {
    return {
      suggestion: null,
      globalFields: GLOBAL_FIELDS,
    }
  },

  watch: {
    suggestion (val) {
      this.$emit('suggestion', val)
    },
    position () {
      // saves a suggestion for later, if the user presses "down"
      this.handleSuggestion(false)
    },
  },

  mounted () {
    // display suggestion if the user it typing a variable
    this.$on('userupdate', () => {
      this.handleSuggestion(true)
    })
  },

  methods: {
    // parses entities
    fromExpression (expression) {
      const segs = []
      // const reg = /([^#@]+)|([#@])([^#@]+):([^#@\s]+)?/g
      const reg = /([^#@]+)|([#@])([^#@]*):(?:([0-9a-zA-Z_]+)|null)?/g
      expression.replace(reg, (_, text, prefix, stext, ref) => {
        if (text) {
          segs.push({
            type: 'text',
            text: text,
            key: Math.random() + '',
          })
        } else {
          segs.push({
            type: prefix === '@' ? 'entity' : 'synonym',
            ref: ref || null,
            text: stext,
            key: Math.random() + '',
          })
        }
        return ''
      })
      return segs
    },
    // back into string
    toExpression (segs) {
      return segs.map((seg) => {
        if (seg.type === 'entity') return '@' + seg.text + ':' + (seg.ref || 'null')
        else if (seg.type === 'synonym') return '#' + seg.text + ':' + (seg.ref || 'null')
        else return seg.text
      }).join('')
    },
    // clean a list of segments, by adding keys and others
    cleanSegments (segs) {
      let out = []
      segs.forEach((seg) => {
        if (!seg) return
        else if (typeof seg === 'string') {
          out.push({
            type: 'text',
            text: seg,
            key: Math.random() + '',
          })
        } else if (seg.type === 'entity' || seg.type === 'synonym') {
          out.push({
            type: seg.type,
            ref: seg.ref || null,
            text: seg.text,
            key: seg.key || Math.random() + '',
          })
        } else if (!seg.text) return
        else {
          out.push({
            type: 'text',
            text: seg.text,
            key: seg.key || Math.random() + '',
          })
        }
      })
      return out
    },
    // check it still looks like an entity
    parseEntity (attrs, methods) {
      let m = attrs.text.match(ENTITY)
      if (!m) {
        methods.push_text(attrs.text)
        return
      }
      if (m[1]) methods.push_text(m[1])
      attrs.text = m[2]
      attrs.ref = attrs.ref || null
      methods.push_seg(attrs)
      if (m[3]) methods.push_text(m[3])
    },
    // look for @ for new entities
    parseText ({ text, key }, methods) {
      text.replace(TEXT, function (_, text, entity) {
        if (text) {
          methods.push_text(text, key)
          key = null
        } else {
          methods.push_seg({
            type: 'entity',
            text: entity,
            key: methods.get_key(),
          })
        }
        return ''
      })
    },
    // check that is still looks like an synonym
    parseSynonym (attrs, methods) {
      let m = attrs.text.match(SYNONYM)
      if (!m) {
        methods.push_text(attrs.text)
        return
      }
      if (m[1]) methods.push_text(m[1])
      attrs.text = m[2] === PLACEHOLDER ? '' : m[2]
      attrs.ref = attrs.ref || null
      methods.push_seg(attrs)
      if (m[3]) methods.push_text(m[3])
    },
    // Adds @, puts PLACEHOLDER if needed
    renderEntity (seg, el, attrs) {
      return el.text('@' + seg.text).attr(attrs)
    },
    // puts PLACEHOLDER if needed
    renderSynonym (seg, el, attrs) {
      return el.text(seg.text || PLACEHOLDER).attr(attrs)
    },
    // calls to check for a new suggestion
    // input = true if it comes from a user input
    handleSuggestion (input) {
      let pos = this.position, suggestion = null
      if (!pos.key) return
      // a variable, suggestion using the current variable name as query
      // the suggestion should be placed under the variable
      if (pos.type === 'entity') {
        suggestion = {
          input: input,
          type: pos.type,
          query: pos.text.substr(1),
          key: pos.key,
          x: pos.el && pos.el.offset().left + pos.el.width() / 2,
        }
      }
      // no suggestion for the cursor, close the suggestion
      if (!suggestion) this.suggestion = null
      // suggestion on input, open suggestions
      else if (input) this.suggestion = suggestion
      // no previous suggestion opened, keep suggestion for "down" pressed
      else if (!this.suggestion || !this.suggestion.input) this.suggestion = suggestion
      // suggestion on a different variable, close and replace the current one
      else if (suggestion.key !== this.suggestion.key ||
          suggestion.type === 'text' && suggestion.left !== this.suggestion.left) {
        this.suggestion = suggestion
      }
    },

    // inserts an entity in a text segment
    insert (entity, key, start, stop, text) {
      if (key === undefined) key = entity.key
      if (start === undefined) start = entity.start
      if (stop === undefined) stop = entity.stop
      if (text === undefined) text = entity.text
      let out = [], found = false
      this.copySegments.forEach((seg) => {
        if (seg.key === key) {
          found = true
          if (start > 0) {
            out.push({
              type: 'text',
              text: seg.text.substring(0, start),
              key: Math.random() + '',
            })
          }
          out.push({
            type: entity.type,
            text: text,
            ref: entity.ref || null,
            key: key,
          })
          if (stop < seg.text.length) {
            out.push({
              type: 'text',
              text: seg.text.substring(stop),
              key: Math.random() + '',
            })
          }
        } else out.push(seg)
      })
      if (found) this.copySegments = out
      else throw new TypeError('key "' + key + '" not found')
    },
    // removes an synonym
    unreference (key) {
      let out = [], found = false, last = null
      this.copySegments.forEach((seg) => {
        if (seg.key === key) {
          found = true
          if (last) {
            last.text += seg.text
          } else {
            last = {
              type: 'text',
              text: seg.text,
              key: Math.random() + '',
            }
            out.push(last)
          }
        } else {
          if (seg.type !== 'text') {
            last = null
            out.push(seg)
          } else if (last) {
            last.text += seg.text
          } else {
            last = seg
            out.push(last)
          }
        }
      })
      if (found) this.copySegments = out
      else throw new TypeError('key "' + key + '" not found')
    },
    // changes the ref of an entity
    setRef (key, val) {
      val = val || null
      let found = false
      this.copySegments.forEach((seg) => {
        if (seg.key === key && seg.type !== 'text') {
          if (seg.ref === val) found = null
          else {
            found = true
            seg.ref = val
          }
        }
      })
      if (found === null) return
      if (!found) throw new TypeError('key "' + key + '" not found')
      // rerender the contenteditable div
      this.renderHTML()
      this.setPosition(this.position.absolute)
      // update history
      this.history.globals[key] = { ref: val }
      // emit an event
      this.$emit('update:segments', this.copySegments)
      // update expression
      this.copyExpression = this.toExpression(this.copySegments)
    },
    // changes the text of an entity
    setText (key, val) {
      let found = false
      this.copySegments.forEach((seg) => {
        if (seg.key === key && seg.type !== 'text') {
          if (seg.text === val) found = null
          else {
            found = true
            seg.text = val
          }
        }
      })
      if (found === null) return
      if (!found) throw new TypeError('key "' + key + '" not found')
      // merge all text edit together
      this.edit = { key: key, diff: 0, right: -1 }
      // rerender the contenteditable div
      this.renderHTML()
      this.setPosition(this.position.absolute)
      // push the new state
      this.pushHistory(this.copySegments)
      // emit an event
      this.$emit('update:segments', this.copySegments)
      // update expression
      this.copyExpression = this.toExpression(this.copySegments)
    },
  },
}

export function stringify (value) {
  return value.replace(/(?:#|(@))([^#@]*):(?:[0-9a-zA-Z_]+|null)?/g, '$1$2')
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  /* style of entities */
  .el-colorable__entity {
    background-color: $entity-bg-color;
  }
  .el-colorable__synonym {
    background-color: $synonym-bg-color;
  }
</style>
